import { createContext, FocusEvent, KeyboardEvent, ReactNode, useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { Tone } from "../Pill";
import s from "./Toast.module.scss";

/** `secondaryLabel` / `onSecondary`: a second action before the main one (e.g. "Write report" next to "Undo"). Both close the toast. */
/** `durationMs`: how long it stays (default 4 s; 8 s with an action). */
type ToastInput = { message: string; tone?: Tone; actionLabel?: string; onAction?: () => void; secondaryLabel?: string; onSecondary?: () => void; durationMs?: number };
type Show = (t: ToastInput) => void;
const Ctx = createContext<Show | null>(null);
const missing: Show = () => {
  if (process.env.NODE_ENV !== "production") console.warn("useToast() outside a ToastProvider: the toast was dropped.");
};
export const useToast = (): Show => useContext(Ctx) ?? missing;

/*
 * The toast layer. An open modal (dialog, sheet, menu) hides everything outside itself from screen readers and keeps
 * keyboard focus inside. So each open kit surface holds a <ToastHost>, and the toast region is placed inside the
 * topmost one: there it is read out, its Undo is in the Tab order, and it sits above the surface. With no surface
 * open the region goes back to where the provider is. Hosts stack in the order they open (the order MUI stacks the
 * modals). The region is one DOM node that is moved with plain DOM calls (React keeps rendering into it through a
 * portal), so opening or closing a dialog re-renders nothing and the region keeps its place as a live region.
 */
const hosts: HTMLElement[] = [];
type Region = { home: HTMLElement; moved: () => void };
const regions = new Map<HTMLElement, Region>(); // region node -> its home in the provider
const avoided = new Set<HTMLElement>();
const MODAL = ".MuiModal-root";

/*
 * The toast never covers the action bar at the bottom of the surface it is in (a dialog's footer, a sheet's actions,
 * a page's sticky footer): `--cy-toast-lift` on the region node = how far that bar reaches up from the screen's
 * bottom edge. Only bars inside the surface that holds the toast count (a page's bar does not lift a toast shown in a
 * dialog over it). Measured whenever the region moves, a bar comes or goes, a toast is shown, or the window resizes.
 */
const liftFor = (at: HTMLElement): number => {
  const surface = at.closest(MODAL);
  const inside = (el: HTMLElement) => (surface ? surface.contains(el) : !el.closest(MODAL));
  let top = Infinity;
  avoided.forEach((el) => {
    if (!el.isConnected || !inside(el)) return;
    const r = el.getBoundingClientRect();
    if (r.height > 0 && r.top < window.innerHeight) top = Math.min(top, r.top);
  });
  return top === Infinity ? 0 : Math.max(0, Math.round(window.innerHeight - top));
};
const relift = () => {
  regions.forEach((_, node) => {
    const at = node.parentElement;
    node.style.setProperty("--cy-toast-lift", `${at ? liftFor(at) : 0}px`);
  });
};
const place = () => {
  const top = hosts[hosts.length - 1];
  regions.forEach(({ home, moved }, node) => {
    const to = top ?? home;
    if (node.parentNode !== to) { to.appendChild(node); moved(); }
  });
  relift();
};
if (typeof window !== "undefined") window.addEventListener("resize", relift);

/**
 * A ref for an action bar the toast must stay above (a sticky footer, a dialog's or sheet's action row).
 * `<footer ref={useToastAvoid()}>`. The kit Modal, ConfirmDialog and SideSheet already register theirs.
 */
export const useToastAvoid = () => {
  const [el, setEl] = useState<HTMLElement | null>(null);
  useLayoutEffect(() => {
    if (!el) return undefined;
    avoided.add(el);
    relift();
    return () => { avoided.delete(el); relift(); };
  }, [el]);
  return setEl as (node: HTMLElement | null) => void;
};

/** Put one inside every modal surface, after its content (so Tab reaches the toast's action last). `active` = open. */
export const ToastHost = ({ active }: { active: boolean }) => {
  const [el, setEl] = useState<HTMLDivElement | null>(null);
  useLayoutEffect(() => {
    if (!active || !el) return undefined;
    hosts.push(el);
    place();
    return () => {
      const at = hosts.lastIndexOf(el);
      if (at >= 0) hosts.splice(at, 1);
      place();
    };
  }, [active, el]);
  return <div ref={setEl} data-toast-host="" />;
};

/**
 * One toast at a time, above every dialog, sheet and menu (see the layer above). It stays while the keyboard is on
 * it (WCAG 2.2.1); after its action focus goes back to where it was before Tab reached the toast.
 *
 * Safe to nest: inside another provider it renders nothing of its own and toasts go to the outer region,
 * so a page can always wrap itself (e.g. under the legacy layout) without doubling toasts.
 */
export const ToastProvider = ({ children }: { children: ReactNode }) => {
  const outer = useContext(Ctx);
  const [toast, setToast] = useState<(ToastInput & { key: number }) | null>(null);
  const [held, setHeld] = useState(false);
  const back = useRef<HTMLElement | null>(null);
  const heldNow = useRef(false);
  heldNow.current = held;
  const show = useCallback((t: ToastInput) => setToast({ ...t, key: Date.now() }), []);
  // The region's own node (moved between hosts) and its home here; created once, on the client only.
  const [node] = useState(() => (typeof document === "undefined" ? null : document.createElement("div")));
  const home = useRef<HTMLDivElement | null>(null);
  useLayoutEffect(() => {
    if (outer || !node || !home.current) return undefined;
    // Moved to another surface: the keyboard is no longer on it, so it may time out again.
    regions.set(node, { home: home.current, moved: () => { if (heldNow.current) setHeld(false); } });
    place();
    return () => { regions.delete(node); node.remove(); };
  }, [outer, node]);
  useEffect(() => {
    if (!toast || held) return undefined;
    const id = window.setTimeout(() => setToast(null), toast.durationMs ?? (toast.onAction || toast.onSecondary ? 8000 : 4000));
    return () => window.clearTimeout(id);
  }, [toast, held]);
  useEffect(() => { if (!toast) setHeld(false); }, [toast]);
  useLayoutEffect(() => { if (toast) relift(); }, [toast]);
  if (outer) return <>{children}</>;

  const onFocus = (e: FocusEvent<HTMLDivElement>) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node | null)) back.current = e.relatedTarget as HTMLElement | null;
    setHeld(true);
  };
  const onBlur = (e: FocusEvent<HTMLDivElement>) => { if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setHeld(false); };
  const dismiss = () => {
    const to = back.current;
    back.current = null;
    setToast(null);
    if (to?.isConnected) to.focus();
  };
  // Escape on the toast closes the toast only (the dialog's own Escape never sees events from this portal anyway).
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== "Escape" || !toast) return;
    e.stopPropagation();
    e.preventDefault();
    dismiss();
  };
  const act = () => { toast?.onAction?.(); dismiss(); };
  const actSecondary = () => { toast?.onSecondary?.(); dismiss(); };
  const region = (
    <div className={`cy-next ${s.region}`} aria-live="polite" onFocus={onFocus} onBlur={onBlur} onKeyDown={onKeyDown}>
      {toast && (
        <div key={toast.key} role="status" className={`${s.toast} ${s[toast.tone ?? "ok"]}`}>
          <span>{toast.message}</span>
          {toast.onSecondary && toast.secondaryLabel && (
            <button type="button" className={s.action} onClick={actSecondary}>{toast.secondaryLabel}</button>
          )}
          {toast.onAction && toast.actionLabel && (
            <button type="button" className={s.action} onClick={act}>{toast.actionLabel}</button>
          )}
        </div>
      )}
    </div>
  );
  return (
    <Ctx.Provider value={show}>
      {children}
      <div ref={home} className={s.home} />
      {node && createPortal(region, node)}
    </Ctx.Provider>
  );
};
